import { useState, useRef } from 'react';
import styles from './index.module.scss';
import SelectAge from './_components/selectAge/SelectAge';
import {
  useGetWhatIfAges,
  useGetWhatIfTopics,
  useGetWhatIfStory,
} from 'src/core/repo/whatif/useCase';
import SelectLanguage from './_components/selectLanguage/SelectLanguage';
import {
  IAgeRange,
  IWhatIfTopic,
  IWhatIfStory,
  IWhatIfLang,
} from 'src/core/repo/whatif/types';
import SelectTopic from './_components/selectTopic/SelectTopic';
import Loading from '@app/statics/Loading';
import { useStore } from '@store';
import { useSearchParams } from 'react-router-dom';
import SelectStoryOptions from './_components/selectStoryOptions/SelectStoryOptions';
import SelectedStory from './_components/selectedStory/SelectedStory';
import StoriesList from './_components/storiesList/StoriesList';
import { useChildList, useInsertChild } from 'src/core/repo/child/useCase';
import { childListResponse } from 'src/core/repo/child/types';
import { Modal } from 'antd';
import AddNewChildForm, { AddNewChildFormRef } from '../myChild/_components/AddNewChildForm/AddNewChildForm';
import { IAddNewChildType } from '../myChild';
import type { FormProps, UploadFile } from 'antd';

const WhatIfPage = () => {
  const [step, setStep] = useState<null | number>(1);
  const [language, setLanguage] = useState<number>();
  const [kidAge, setKidAge] = useState<number>();
  const [topic, setTopic] = useState<IWhatIfTopic>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userStoryText, setUserStoryText] = useState<string>('');
  const [selectedStory, setSelectedStory] = useState<IWhatIfStory | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const getStories = useGetWhatIfStory();
  const stories: IWhatIfStory[] | undefined = getStories.data;
  const getWhatIfAgesMutation = useGetWhatIfAges();
  const getWhatIfTopics = useGetWhatIfTopics();
  const ages: IAgeRange[] = getWhatIfAgesMutation.data ?? [];
  const topics: IWhatIfTopic[] = getWhatIfTopics.data ?? [];
  const getChildList = useChildList();
  const children: childListResponse[] = getChildList.data ?? [];
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const addChildFormRef = useRef<AddNewChildFormRef>(null);

  const handleSelectLanguage = (language: IWhatIfLang) => {
    setLanguage(language.LanguageID);
    setSearchParams({ lang: language.Lang.split(' ')[0] });
    getChildList.mutateAsync({});
    setStep(2);
  };

  const { mutateAsync: insertChild } = useInsertChild();

  const handleSelectAge = (ageId: number) => {
    setKidAge(ageId);
    if (language == null) return;
    const data = {
      lang_id: language,
      child_id: ageId,
    };
    getWhatIfTopics.mutate(data);

    if (topics) {
      setStep(3);
    }
  };

  const handleSelectTopic = (topic: IWhatIfTopic) => {
    setTopic(topic);
    if (language == null || kidAge == null) return;
    const data = {
      lang_id: language,
      child_id: 6,
      topic_id: topic.TrainingTopicID,
      age_id: topic.AgeRangeID,
    };
    getStories.mutate(data);
    setStep(4);
  };
  const { theme = 'day' } = useStore();

  const handleSelectImage = (story: IWhatIfStory) => {
    setSelectedStory(story);
    setStep(5);
  };

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const onFinish: FormProps<IAddNewChildType>['onFinish'] = (values) => {
    insertChild(
      {
        child_name: values.childName!,
        birth_date: values.birthDate!.format('YYYY-MM-DD'),
        gender_id: parseFloat(values.gender!),
        photo: values?.photo?.[0]?.thumbUrl || '',
      },
      {
        onSuccess: () => {
          getChildList.mutateAsync({});
          addChildFormRef.current?.resetForm();
        },
      },
    );
    setIsDialogOpen(false);
  };

  const onFinishFailed: FormProps<IAddNewChildType>['onFinishFailed'] = (
    errorInfo,
  ) => console.log('Failed:', errorInfo);

  return (
    <div className={styles.container}>
      {step === 1 ? (
        <SelectLanguage
          searchParams={searchParams}
          theme={theme}
          onSelectLanguage={handleSelectLanguage}
        />
      ) : step === 2 ? (
        <SelectAge
          openDialog={handleOpenDialog}
          searchParams={searchParams}
          theme={theme}
          ages={children}
          selectedAgeId={kidAge}
          onSelectAge={handleSelectAge}
          onBack={() => setStep(1)}
        />
      ) : step === 3 ? (
        <SelectTopic
          theme={theme}
          handleSelectTopic={handleSelectTopic}
          topics={topics}
          onBack={() => setStep(2)}
        />
      ) : step === 4 ? (
        getStories.isLoading || !stories ? (
          <Loading />
        ) : (
          <StoriesList
            onBack={() => setStep(3)}
            theme={theme}
            stories={stories}
            setSelectedStory={handleSelectImage}
          />
        )
      ) : step === 5 ? (
        <SelectedStory
          setFile={setFile}
          story={selectedStory!}
          userStoryText={userStoryText}
          setUserStoryText={setUserStoryText}
          setStep={setStep}
        />
      ) : step === 6 ? (
        <SelectStoryOptions
          setStep={setStep}
          selectedTopic={topic}
          selectedStory={selectedStory}
          userStoryText={userStoryText}
        />
      ) : null}

      <Modal
        className={styles.modal}
        title="Add New Child"
        open={isDialogOpen}
        onCancel={handleCloseDialog}
        footer={null}>
        <AddNewChildForm
          ref={addChildFormRef}
          fileList={fileList}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          setFileList={setFileList}
        />
      </Modal>
    </div>
  );
};

export default WhatIfPage;
